import React from 'react';
import { Link } from 'react-router-dom';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'primary' | 'secondary' | 'outline' | 'ghost' | 'link';
  size?: 'sm' | 'md' | 'lg';
  asChild?: boolean;
  href?: string;
  isExternal?: boolean;
}

const variantStyles = {
  default: 'bg-background-alt text-text hover:bg-section-dark focus:ring-background-alt',
  primary: 'bg-primary text-text-light hover:bg-primary/90 focus:ring-primary',
  secondary: 'bg-secondary text-text-light hover:bg-secondary/90 focus:ring-secondary',
  outline: 'border border-primary bg-transparent text-primary hover:bg-primary/10 focus:ring-primary',
  ghost: 'bg-transparent hover:bg-section-light text-text hover:text-primary focus:ring-background-alt',
  link: 'bg-transparent underline-offset-4 hover:underline text-primary hover:text-primary/90 focus:ring-transparent p-0 h-auto',
};

const sizeStyles = {
  sm: 'text-sm px-3 py-1.5 rounded',
  md: 'text-base px-5 py-2.5 rounded-md',
  lg: 'text-lg px-6 py-3 rounded-md',
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className = '', variant = 'default', size = 'md', asChild = false, href, isExternal = false, children, ...props }, ref) => {
    const variantClasses = variantStyles[variant];
    const sizeClasses = sizeStyles[size];
    const baseClasses = 
      'inline-flex items-center justify-center font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none';
    
    const allClasses = `${baseClasses} ${variantClasses} ${sizeClasses} ${className}`;

    if (asChild && href) {
      if (isExternal) {
        return (
          <a 
            href={href}
            className={allClasses}
            target="_blank"
            rel="noopener noreferrer"
          >
            {children}
          </a>
        );
      }
      return (
        <Link 
          to={href}
          className={allClasses}
        >
          {children}
        </Link>
      );
    }

    if (href) {
      if (isExternal) {
        return (
          <a 
            href={href}
            className={allClasses}
            target="_blank"
            rel="noopener noreferrer"
          >
            {children}
          </a>
        );
      }
      return (
        <Link 
          to={href}
          className={allClasses}
        >
          {children}
        </Link>
      );
    }

    return (
      <button
        className={allClasses}
        ref={ref}
        {...props}
      >
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

export { Button }; 